import { ComponentProps, FC } from 'react'

import { useFragment } from '@apollo/client'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { BankingCardApprovableFragmentDoc } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'
import NoteField from 'modules/inbox_tasks/components/NoteField'

import { translateCardType, translatePeriod } from './helpers'

type BankingCardTaskContentProps = {
  id: string
  requesterContent: EmployeeTableViewRow
}

const BankingCardTaskContent: FC<BankingCardTaskContentProps> = ({ id, requesterContent }) => {
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()

  const { data } = useFragment({
    fragment: BankingCardApprovableFragmentDoc,
    fragmentName: 'BankingCardApprovable',
    from: {
      __typename: 'BankingCard',
      id,
    },
    optimistic: false,
  })

  if (!data) {
    return null
  }

  const cardType = data.bankingCardType ? translateCardType(data.bankingCardType) : undefined

  const cardTypeContent = cardType
    ? {
        text: cardType,
        type: 'item' as const,
      }
    : null

  const cardNumberContent = data.last4
    ? {
        text: `**** **** **** ${data.last4}`,
        type: 'item' as const,
      }
    : null

  const cardholder = data.cardholder?.employee?.access?.fullName

  const cardholderContent = cardholder
    ? {
        text: cardholder,
        type: 'item' as const,
      }
    : null

  const displayLimitAmount = data.cardLimit?.limitForCurrentPeriod
    ? MoneyWithCurrency({
        amount: data.cardLimit.limitForCurrentPeriod,
        currency: data.cardLimit?.currency,
        locale: accessLocale,
      })
    : undefined

  const translatedPeriod = data.cardLimit?.period
    ? translatePeriod(data.cardLimit.period)
    : undefined

  const displayBudget = displayLimitAmount
    ? `${displayLimitAmount} ${translatedPeriod?.toLowerCase()}`
    : undefined

  const budgetContent = displayBudget
    ? {
        text: displayBudget,
        type: 'item' as const,
      }
    : null

  const periodContent = data.cardLimit?.period
    ? {
        text: data.cardLimit.period,
        type: 'item' as const,
      }
    : null

  const softwareContent = data.softwareSetup?.software?.name
    ? {
        text: data.softwareSetup.software.name,
        type: 'item' as const,
      }
    : null

  const details: ComponentProps<typeof DetailsItemsList>['details'] = [
    requesterContent &&
      data.alias && {
        title: i18n.t('tasks.approvals.banking_card.fields.card_name'),
        content: {
          text: data.alias,
          type: 'item' as const,
        },
      },
    cardNumberContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.card_number'),
      content: cardNumberContent,
    },
    cardTypeContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.card_type'),
      content: cardTypeContent,
    },
    cardholderContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.cardholder'),
      content: cardholderContent,
    },
    budgetContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.budget'),
      content: budgetContent,
    },
    periodContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.period'),
      content: periodContent,
    },
    softwareContent && {
      title: i18n.t('tasks.approvals.banking_card.fields.software'),
      content: softwareContent,
    },
  ].filter(Boolean)

  return (
    <>
      <DetailsItemsList details={details} tableView />
      {!!data.description && (
        <NoteField
          label={i18n.t('tasks.approvals.banking_card.fields.description')}
          content={data.description}
        />
      )}
    </>
  )
}

export default BankingCardTaskContent

export * from './metadata'
